import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { CheckCircle2, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { submitGhlWebhookLead } from "@/lib/ghl-config.functions";

const FLOOR_TYPES = ["Garage", "Basement", "Commercial", "Patio & Deck", "Workshop", "Other"] as const;
const SIZES = [
  "Single car (~250 sq ft)",
  "Double car (~400 sq ft)",
  "Triple car (~750 sq ft)",
  "1,000+ sq ft",
  "Not sure",
] as const;
const TIMELINES = ["ASAP", "Next 30 days", "1–2 months", "Just exploring"] as const;

const stepOneSchema = z.object({
  what_area_do_you_want_to_coat: z.string().min(1, "Choose a floor type"),
  what_is_the_approximate_size_of_the_area: z.string().min(1, "Select a size"),
  timeline_for_project: z.string().min(1, "Select a timeline"),
});

const stepTwoSchema = z.object({
  full_name: z.string().trim().min(2, "Enter your full name").max(80),
  email: z.string().trim().email("Enter a valid email").max(160),
  phone: z.string().trim().min(7, "Enter a valid phone").max(20),
  postal_code: z
    .string()
    .trim()
    .max(10)
    .regex(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, "Enter a valid postal code (e.g. T2P 1J9)"),
});

type StepOne = z.infer<typeof stepOneSchema>;
type StepTwo = z.infer<typeof stepTwoSchema>;

type Props = {
  /** When true, render only the floor-type picker (used in hero). */
  heroMode?: boolean;
  /** Initial floor type, e.g. when arriving from the hero picker. */
  initialFloorType?: string;
  /** Called when hero step is confirmed — parent should scroll to #quote. */
  onHeroSelect?: (floorType: string) => void;
};

/** Off-screen but DOM-present — GHL external tracker reads these; CSS-hidden inputs are skipped. */
const offscreenStyle: React.CSSProperties = {
  position: "absolute",
  left: "-10000px",
  top: "auto",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  opacity: 0,
};

function splitName(full: string): { first: string; last: string } {
  const trimmed = full.trim();
  const idx = trimmed.indexOf(" ");
  if (idx === -1) return { first: trimmed, last: "" };
  return { first: trimmed.slice(0, idx), last: trimmed.slice(idx + 1).trim() };
}

export function MultiStepQuoteForm({ heroMode = false, initialFloorType = "", onHeroSelect }: Props) {
  const navigate = useNavigate();
  const submitLead = useServerFn(submitGhlWebhookLead);

  const [step, setStep] = useState<1 | 2>(1);
  const [stepOne, setStepOne] = useState<StepOne>({
    what_area_do_you_want_to_coat: initialFloorType,
    what_is_the_approximate_size_of_the_area: "",
    timeline_for_project: "",
  });
  const [stepTwo, setStepTwo] = useState<StepTwo>({
    full_name: "",
    email: "",
    phone: "",
    postal_code: "",
  });
  const [stepOneErr, setStepOneErr] = useState<Partial<Record<keyof StepOne, string>>>({});
  const [stepTwoErr, setStepTwoErr] = useState<Partial<Record<keyof StepTwo, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const formRef = useRef<HTMLFormElement | null>(null);
  const submissionIdRef = useRef<string>("");
  if (!submissionIdRef.current) {
    submissionIdRef.current =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `sub-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  const submittingRef = useRef(false);

  // Single submission path: the server function forwards to the GHL webhook.
  // The GHL external-tracking script must NOT also capture this form, or every
  // lead is created twice. We never fire a native submit event (see below).



  // Sync floor type selected in the hero short form into the main form.
  useEffect(() => {
    if (heroMode || !initialFloorType) return;
    setStepOne((s) =>
      s.what_area_do_you_want_to_coat
        ? s
        : { ...s, what_area_do_you_want_to_coat: initialFloorType }
    );
  }, [initialFloorType, heroMode]);



  // ---------- HERO MODE ----------
  if (heroMode) {
    return (
      <div className="rounded-md border border-[#121212]/10 bg-white p-6 md:p-7 shadow-2xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-copper/15 border border-copper/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-copper">
          Takes ~30 seconds
        </div>
        <h2 className="mt-4 text-2xl text-[#121212]">Get a Free Quote</h2>
        <p className="mt-1 text-sm text-[#121212]/70">What kind of floor are you looking to coat?</p>
        <div className="mt-5 grid grid-cols-2 gap-2.5">
          {FLOOR_TYPES.map((t) => {
            const active = stepOne.what_area_do_you_want_to_coat === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setStepOne((s) => ({ ...s, what_area_do_you_want_to_coat: t }))}
                className={`rounded-sm border px-3 py-3 text-sm font-semibold transition-colors ${
                  active
                    ? "border-copper bg-copper text-white"
                    : "border-[#121212]/15 bg-white text-[#121212] hover:border-copper hover:text-copper"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          disabled={!stepOne.what_area_do_you_want_to_coat}
          onClick={() => onHeroSelect?.(stepOne.what_area_do_you_want_to_coat)}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-copper px-6 py-4 text-sm font-bold uppercase tracking-wider text-white hover:bg-copper-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </button>
        <p className="mt-3 text-center text-xs text-[#121212]/70">No obligation · Free on-site measurement</p>
      </div>
    );
  }

  // ---------- FULL FORM ----------
  if (done) {
    return (
      <div className="rounded-sm border border-copper/40 bg-white p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-copper" />
        <h3 className="mt-4 text-2xl text-[#121212]">Quote Request Received</h3>
        <p className="mt-2 text-[#121212]/70">
          We&rsquo;ll call within one business day to schedule your free on-site measurement.
        </p>
      </div>
    );
  }

  const fieldCls =
    "w-full rounded-sm border border-[#121212]/15 bg-white px-4 py-3 text-[#121212] placeholder:text-[#525252] focus:border-copper focus:outline-none focus:ring-1 focus:ring-copper";
  const labelCls = "block text-xs font-semibold uppercase tracking-widest text-[#121212]/70 mb-1.5";

  function handleContinue() {
    const parsed = stepOneSchema.safeParse(stepOne);
    if (!parsed.success) {
      const next: Partial<Record<keyof StepOne, string>> = {};
      for (const i of parsed.error.issues) next[i.path[0] as keyof StepOne] ||= i.message;
      setStepOneErr(next);
      return;
    }
    setStepOneErr({});
    setStep(2);
  }

  async function handleSubmit() {
    const form = formRef.current;
    if (!form) return;

    // Synchronous re-entry guard.
    if (submittingRef.current) return;

    // Validate both steps.
    const oneParsed = stepOneSchema.safeParse(stepOne);
    const twoParsed = stepTwoSchema.safeParse(stepTwo);
    if (!oneParsed.success || !twoParsed.success) {
      if (!oneParsed.success) {
        const next: Partial<Record<keyof StepOne, string>> = {};
        for (const i of oneParsed.error.issues) next[i.path[0] as keyof StepOne] ||= i.message;
        setStepOneErr(next);
        setStep(1);
      }
      if (!twoParsed.success) {
        const next: Partial<Record<keyof StepTwo, string>> = {};
        for (const i of twoParsed.error.issues) next[i.path[0] as keyof StepTwo] ||= i.message;
        setStepTwoErr(next);
      }
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);

    const fd = new FormData(form);
    const params = new URLSearchParams(window.location.search);
    const payload: Record<string, string> = {
      submission_id: submissionIdRef.current,

      full_name: String(fd.get("full_name") ?? ""),
      first_name: String(fd.get("first_name") ?? ""),
      last_name: String(fd.get("last_name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      postal_code: String(fd.get("postal_code") ?? ""),
      what_area_do_you_want_to_coat: String(fd.get("what_area_do_you_want_to_coat") ?? ""),
      what_is_the_approximate_size_of_the_area: String(fd.get("what_is_the_approximate_size_of_the_area") ?? ""),
      timeline_for_project: String(fd.get("timeline_for_project") ?? ""),
      utm_source: params.get("utm_source") ?? "",
      utm_medium: params.get("utm_medium") ?? "",
      utm_campaign: params.get("utm_campaign") ?? "",
      utm_term: params.get("utm_term") ?? "",
      utm_content: params.get("utm_content") ?? "",
    };

    try {
      const result = await submitLead({ data: payload });
      if (!result.success) throw new Error(result.error ?? "Submission failed");
      navigate({ to: "/thank-you" });
    } catch (err) {
      console.error("Lead submission failed:", err);
      submittingRef.current = false;
      setSubmitting(false);
      setSubmitError("Submission failed. Please try again or call us directly.");
    }
  }


  const { first: firstName, last: lastName } = splitName(stepTwo.full_name);

  return (
    <form
      ref={formRef}
      noValidate
      data-ghl-ignore="true"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="mb-6 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-widest text-copper">
          Step {step} of 2
        </p>
        <div className="flex gap-1.5">
          <span className={`h-1 w-10 rounded-full ${step >= 1 ? "bg-copper" : "bg-[#121212]/15"}`} />
          <span className={`h-1 w-10 rounded-full ${step >= 2 ? "bg-copper" : "bg-[#121212]/15"}`} />
        </div>
      </div>

      {/* ----- STEP 1 (visible) ----- */}
      <div className={step === 1 ? "grid gap-5" : "hidden"}>
        <div>
          <label className={labelCls}>Floor type *</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FLOOR_TYPES.map((t) => {
              const active = stepOne.what_area_do_you_want_to_coat === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setStepOne((s) => ({ ...s, what_area_do_you_want_to_coat: t }))}
                  className={`rounded-sm border px-3 py-2.5 text-sm font-semibold transition-colors ${
                    active
                      ? "border-copper bg-copper text-white"
                      : "border-[#121212]/15 bg-white text-[#121212] hover:border-copper hover:text-copper"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          {stepOneErr.what_area_do_you_want_to_coat && (
            <p className="mt-1 text-xs text-destructive">{stepOneErr.what_area_do_you_want_to_coat}</p>
          )}
        </div>

        <div>
          <label className={labelCls} htmlFor="qf-size">Approximate size *</label>
          <select
            id="qf-size"
            name="what_is_the_approximate_size_of_the_area"
            className={fieldCls}
            value={stepOne.what_is_the_approximate_size_of_the_area}
            onChange={(e) =>
              setStepOne((s) => ({ ...s, what_is_the_approximate_size_of_the_area: e.target.value }))
            }
          >
            <option value="" disabled>Select size…</option>
            {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {stepOneErr.what_is_the_approximate_size_of_the_area && (
            <p className="mt-1 text-xs text-destructive">{stepOneErr.what_is_the_approximate_size_of_the_area}</p>
          )}
        </div>

        <div>
          <label className={labelCls} htmlFor="qf-timeline">Project timeline *</label>
          <select
            id="qf-timeline"
            name="timeline_for_project"
            className={fieldCls}
            value={stepOne.timeline_for_project}
            onChange={(e) => setStepOne((s) => ({ ...s, timeline_for_project: e.target.value }))}
          >
            <option value="" disabled>Select timeline…</option>
            {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {stepOneErr.timeline_for_project && (
            <p className="mt-1 text-xs text-destructive">{stepOneErr.timeline_for_project}</p>
          )}
        </div>

        <button
          type="button"
          onClick={handleContinue}
          className="inline-flex items-center justify-center gap-2 rounded-sm bg-copper px-6 py-4 text-sm font-bold uppercase tracking-wider text-white hover:bg-copper-hover transition-colors"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {/* ----- STEP 2 (visible) ----- */}
      <div className={step === 2 ? "grid gap-4" : "hidden"}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="qf-name">Full name *</label>
            <input
              id="qf-name"
              name="full_name"
              type="text"
              autoComplete="name"
              className={fieldCls}
              placeholder="Jane Smith"
              value={stepTwo.full_name}
              onChange={(e) => setStepTwo((s) => ({ ...s, full_name: e.target.value }))}
            />
            {stepTwoErr.full_name && <p className="mt-1 text-xs text-destructive">{stepTwoErr.full_name}</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="qf-email">Email *</label>
            <input
              id="qf-email"
              name="email"
              type="email"
              autoComplete="email"
              className={fieldCls}
              placeholder="jane@example.com"
              value={stepTwo.email}
              onChange={(e) => setStepTwo((s) => ({ ...s, email: e.target.value }))}
            />
            {stepTwoErr.email && <p className="mt-1 text-xs text-destructive">{stepTwoErr.email}</p>}
          </div>
          <div>
            <label className={labelCls} htmlFor="qf-phone">Phone *</label>
            <input
              id="qf-phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              className={fieldCls}
              placeholder="(703) 555-0143"
              value={stepTwo.phone}
              onChange={(e) => setStepTwo((s) => ({ ...s, phone: e.target.value }))}
            />
            {stepTwoErr.phone && <p className="mt-1 text-xs text-destructive">{stepTwoErr.phone}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="qf-postal-code">Postal Code *</label>
            <input
              id="qf-postal-code"
              name="postal_code"
              type="text"
              inputMode="text"
              autoComplete="postal-code"
              maxLength={7}
              className={fieldCls}
              placeholder="T2P 1J9"
              value={stepTwo.postal_code}
              onChange={(e) => setStepTwo((s) => ({ ...s, postal_code: e.target.value.toUpperCase() }))}
            />
            {stepTwoErr.postal_code && <p className="mt-1 text-xs text-destructive">{stepTwoErr.postal_code}</p>}
          </div>
        </div>

        {submitError && (
          <p className="text-sm text-destructive" role="alert">{submitError}</p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={
            submitting ||
            !stepTwo.full_name.trim() ||
            !stepTwo.email.trim() ||
            !stepTwo.phone.trim() ||
            !stepTwo.postal_code.trim()
          }
          className="inline-flex items-center justify-center gap-2 rounded-sm bg-copper px-6 py-4 text-sm font-bold uppercase tracking-wider text-white hover:bg-copper-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Get My Free Quote
        </button>

        <button
          type="button"
          onClick={() => setStep(1)}
          className="inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#121212]/70 hover:text-copper transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to project details
        </button>
        <p className="text-center text-xs text-[#121212]/70">We&rsquo;ll call within 1 business day.</p>
      </div>

      {/* ----- Off-screen mirror for the chip-style floor-type control. GHL skips display:none. ----- */}
      <input
        name="what_area_do_you_want_to_coat"
        type="text"
        value={stepOne.what_area_do_you_want_to_coat}
        onChange={() => { /* mirror only */ }}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={offscreenStyle}
      />

      {/* ----- Off-screen first/last name mirrors derived from full_name. ----- */}
      <input
        name="first_name"
        type="text"
        value={firstName}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={offscreenStyle}
        onChange={() => { /* mirror only */ }}
      />
      <input
        name="last_name"
        type="text"
        value={lastName}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={offscreenStyle}
        onChange={() => { /* mirror only */ }}
      />

      {/* ----- Dedupe key — same value across retries (useRef). ----- */}
      <input type="hidden" name="submission_id" value={submissionIdRef.current} readOnly />
    </form>
  );
}
